/**
 * The "Add person" form as it has always been.
 *
 * **Nothing in here changes.** Centre Managers enter people on this form every day and the new
 * registration form is behind a toggle precisely so their workflow is not altered underneath
 * them — so the mutation, the fields and the validation are exactly what they were, and this
 * file should stay a historical copy rather than a shared base to refactor against.
 *
 * The one edit made when the toggle arrived: the page shell (ResponsiveContainer / Segment /
 * card / header) moved to `index.tsx`, so both forms sit inside the same card and the toggle
 * has one place to live. This component now returns the `<Form>` alone.
 */

import { useState } from 'react'
import { gql, useMutation, useQuery, useLazyQuery } from '@apollo/client';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { formatDateForBirth } from "../../../dates";

import { PersonIdPicker } from '../PersonIdPicker'
import { PATHS } from '../../../paths';

import {
  Button,
  Form,
  FormInput,
  FormField,
  Checkbox,
  Radio,
  Input,
  Message,
} from 'semantic-ui-react'
import { useNavigate } from 'react-router-dom';

const CREATE_PERSON = gql`
mutation AdminCreateUser($name: String!, $preferredName: String, $picturePermission: Boolean!, $address: String, $scriptName: String, $identifier: String, $tagIds: [Int!], $sex: Int, $phone: String, $dob: String) {
  adminCreateUser(name: $name, preferredName: $preferredName, picturePermission: $picturePermission, address: $address, scriptName: $scriptName, identifier: $identifier, tagIds: $tagIds, sex: $sex, phone: $phone, dob: $dob) {
      id
  }
}`;

const GET_ORG_SCRIPT_LABEL = gql`
query OrganisationScriptLabel {
  me {
    organisation {
      scriptLabel
    }
  }
}`;

const GET_UPLOAD_URLS = gql`
query PersonUploadUrls($personId: Int!) {
  user(personId: $personId) {
    id
    uploadURL
    uploadURLSmall
  }
}`;

const BIOLOGICAL_SEX_ISO_CODES = [
    { key: 0, text: 'Unknown', value: 0 },
    { key: 1, text: 'Male', value: 1 },
    { key: 2, text: 'Female', value: 2 },
    { key: 9, text: 'N/A', value: 9 },
]

const ClassicPersonCreator = () => {

    const [name, setName] = useState("");
    const [preferredName, setPreferredName] = useState("");
    const [sex, setSex] = useState(0);
    const [address, setAddress] = useState("");
    const [scriptName, setScriptName] = useState("");
    const [phone, setPhone] = useState("");
    const [identifier, setIdentifier] = useState("");
    const [tagIds, setTagIds] = useState<number[]>([]);
    const [picturePermission, setPicturePermission] = useState(false);
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [dob, setDob] = useState<Date | null>(null);
    const [graphqlDob, setGraphqlDob] = useState<string | "">("");
    const [showErrors, setShowErrors] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [createPersonMutation, { loading }] = useMutation(CREATE_PERSON);
    const [fetchUploadUrls] = useLazyQuery(GET_UPLOAD_URLS, { fetchPolicy: 'network-only' });
    const navigate = useNavigate()

    const { data: orgData } = useQuery(GET_ORG_SCRIPT_LABEL);
    const scriptLabel = orgData?.me?.organisation?.scriptLabel || 'Bengali';

    const isComplete = !!(name && address && graphqlDob && identifier);

    const uploadPhoto = async (personId: number) => {
        if (!photoFile) return;
        try {
            const { data } = await fetchUploadUrls({ variables: { personId } });
            const urls = [data?.user?.uploadURL, data?.user?.uploadURLSmall].filter(Boolean) as string[];
            await Promise.all(urls.map((url) => fetch(url, {
                method: 'PUT',
                body: photoFile,
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'Content-Length': photoFile.size.toString(),
                },
                referrer: 'no-referrer',
            })));
        } catch (photoErr) {
            // The person was created successfully; only the photo failed. Don't block navigation.
            console.error('Photo upload failed', photoErr);
        }
    }

    const handleSave = async () => {
        if (!isComplete) {
            setShowErrors(true);
            return;
        }

        setErrorMessage("");

        try {
            const result = await createPersonMutation({ variables: {
                name,
                preferredName,
                address,
                scriptName,
                identifier,
                sex,
                tagIds,
                phone,
                dob: graphqlDob,
                picturePermission,
            }});

            const newId = result.data.adminCreateUser.id;

            await uploadPhoto(newId);

            navigate(`/people/${newId}`);
        } catch (e: any) {
            setErrorMessage(e?.message || 'Something went wrong creating this person.');
        }
    }

  return (
            <Form error={!!errorMessage} size='small'>
                <FormField>
                    <label>Identifier</label>
                    <PersonIdPicker
                        setId={setIdentifier}
                        setTagIds={setTagIds}
                        showErrors={showErrors}
                    />
                    <Input
                        fluid
                        readOnly
                        className='identifier-display'
                        value={identifier}
                        placeholder='(Automatically allocated)'
                    />
                </FormField>
                <Form.Group widths='equal'>
                    <FormInput
                        error={showErrors && name.length < 1 && 'Please enter a name'}
                        fluid
                        label='Name'
                        placeholder='Name'
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <FormInput
                        fluid
                        label='Preferred name'
                        placeholder='Preferred name'
                        value={preferredName}
                        onChange={(e) => setPreferredName(e.target.value)}
                    />
                </Form.Group>
                <Form.Group widths='equal'>
                    <FormInput
                        fluid
                        label={`Name in ${scriptLabel}`}
                        placeholder=''
                        value={scriptName}
                        onChange={(e) => setScriptName(e.target.value)}
                    />
                    <FormInput
                        fluid
                        label='Phone'
                        placeholder='Phone number'
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    />
                </Form.Group>
                <Form.Group widths='equal'>
                    <FormField
                        error={showErrors && graphqlDob.length < 1}
                    >
                        <label>Date of birth</label>
                        <DatePicker
                            selected={dob}
                            dateFormat="dd/MM/yyyy"
                            onChange={(date) => {
                                setGraphqlDob(date != null ? formatDateForBirth(date?.getTime()) : "")
                                setDob(date);
                            }}
                            showYearDropdown
                            maxDate={new Date()}
                        />
                        {showErrors && graphqlDob.length < 1 && (
                            <div className='ui pointing above prompt label'>Please enter a date of birth</div>
                        )}
                    </FormField>
                    <FormField>
                        <label>Sex</label>
                        <div className='sex-options'>
                            {BIOLOGICAL_SEX_ISO_CODES.map((option) => (
                                <Radio
                                    key={option.key}
                                    label={option.text}
                                    name='sexRadioGroup'
                                    value={option.value.toString()}
                                    checked={sex === option.value}
                                    onChange={() => setSex(option.value)}
                                />
                            ))}
                        </div>
                    </FormField>
                </Form.Group>
                <FormInput
                    error={showErrors && address.length < 1 && 'Please enter an address'}
                    fluid
                    label='Address'
                    placeholder='Address'
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                />
                <Form.Group widths='equal'>
                    <FormField>
                        <label>Photo</label>
                        <input
                            type='file'
                            accept='image/*'
                            onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                        />
                    </FormField>
                    <FormField>
                        <label>Permissions</label>
                        <div className='toggles'>
                            <Checkbox
                                toggle
                                label='Picture permission'
                                checked={picturePermission}
                                onChange={() => setPicturePermission(!picturePermission)}
                            />
                        </div>
                    </FormField>
                </Form.Group>
                <Message
                    error
                    header='Could not save'
                    content={errorMessage}
                />
                <Button
                    primary
                    type='button'
                    loading={loading}
                    disabled={loading || !isComplete}
                    onClick={() => handleSave()}>
                    Save
                </Button>
                <Button
                    type='button'
                    basic
                    disabled={loading}
                    onClick={() => navigate(PATHS.PEOPLE)}>
                    Cancel
                </Button>
            </Form>
  )
}

export { ClassicPersonCreator }
